/**
 * Free gift that puts itself in the cart.
 *
 * The money side is not ours: an automatic discount in the admin zeroes the gift
 * when a qualifying product is present. This only saves the shopper from having to
 * add it by hand — and, just as importantly, takes it back out when the qualifying
 * product leaves, because a gift left behind stops being discounted and quietly
 * turns into a 99 shekel line nobody chose.
 *
 * The gift line carries a hidden `_gift` property. That is what tells it apart from
 * the same product bought deliberately: without it we would remove an item the
 * shopper paid for. Properties starting with an underscore are hidden at checkout,
 * so the line reads as an ordinary product at 0.
 *
 * How the gift LOOKS in the drawer is not this file's business any more: Liquid can
 * read the same flag, so snippets/cart-drawer.liquid renders the gift block itself.
 * What remains here is the cart page, whose markup we do not own.
 *
 * Cart mutations are caught by wrapping fetch rather than by listening to theme
 * events: the theme has several components that touch the cart, and every one of
 * them goes through fetch. Our own requests use the saved original, so they never
 * re-enter. After a change lands the theme is asked to redraw itself through its
 * own event bus — we render nothing.
 *
 * Config comes from snippets/gift-auto-add.liquid (theme settings).
 */
(() => {
  const config = window.themeGiftConfig;
  if (!config?.enabled || !config.variantId || !config.qualifyingIds?.length) return;

  const GIFT_FLAG = '_gift';
  const qualifying = new Set(config.qualifyingIds);
  const root = window.Shopify?.routes?.root ?? '/';
  const nativeFetch = window.fetch.bind(window);

  let working = false;

  const isCartMutation = (url) => /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url);

  const isGiftProduct = (item) => (item.variant_id ?? item.id) === config.variantId;

  const postJson = (path, body) =>
    nativeFetch(root + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });

  const readCart = async () => {
    const response = await nativeFetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`cart.js responded ${response.status}`);
    return response.json();
  };

  /** Ask the theme to redraw. On the cart page there is no drawer to redraw and the
   *  markup was rendered before our change, so the page has to come back fresh. */
  const refresh = async () => {
    if (/\/cart\/?$/.test(window.location.pathname)) {
      window.location.reload();
      return;
    }

    window.eventBus?.emit('render:cart:drawer');

    try {
      window.wetheme?.updateCartCount?.(await readCart());
    } catch {
      /* The count is cosmetic; a failure here must not surface to the shopper. */
    }
  };

  const reconcile = async () => {
    if (working) return;
    working = true;

    try {
      const cart = await readCart();
      const gift = cart.items.find((item) => item.properties?.[GIFT_FLAG]);
      const qualifies = cart.items.some(
        (item) => !item.properties?.[GIFT_FLAG] && qualifying.has(item.product_id)
      );

      let changed = false;

      // Never a second line of the same product. A shopper who had already put the
      // serum in the cart used to end up with two: the discount frees exactly one,
      // and Shopify picks which — it picked theirs, leaving the line we had labelled
      // "gift" as the one being charged. One line cannot be mislabelled.
      if (qualifies && !gift && !cart.items.some(isGiftProduct)) {
        // Out of stock is a real possibility and not an error worth showing: the
        // shopper still gets the product they came for.
        const added = await postJson('cart/add.js', {
          items: [{ id: config.variantId, quantity: 1, properties: { [GIFT_FLAG]: 'true' } }]
        });
        changed = added.ok;
      } else if (!qualifies && gift) {
        await postJson('cart/change.js', { id: gift.key, quantity: 0 });
        changed = true;
      } else if (gift && gift.quantity > 1) {
        // The discount frees exactly one. A second copy would be charged in full.
        await postJson('cart/change.js', { id: gift.key, quantity: 1 });
        changed = true;
      }

      if (changed) await refresh();
    } catch (error) {
      console.error('Gift auto-add:', error);
    } finally {
      working = false;
    }
  };

  /** Reconciling on load is not belt and braces, it is the only cover for the ways
   *  a cart changes without a fetch. The cart page removes a line through a plain
   *  link (`/cart/change?line=…`), the browser navigates, and no wrapper of ours
   *  ever runs — the gift stayed behind, no longer discounted, priced at full. The
   *  same goes for a shopper returning days later to a cart left in the cookie. */
  const start = () => {
    reconcile();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  window.fetch = async (...args) => {
    const response = await nativeFetch(...args);

    const target = args[0];
    const url = typeof target === 'string' ? target : (target?.url ?? '');

    // Not awaited on purpose: the theme is waiting on this response to draw the
    // drawer, and holding it back would show a stalled cart.
    if (!working && response.ok && isCartMutation(String(url))) reconcile();

    return response;
  };
})();
